import React, { useEffect, useState } from "react";

import { Formik, Form } from "formik";
import { isEmpty } from "ramda";

import {
  Button,
  Pane,
  Radio,
  Steps,
  Typography,
} from "clearsense-ui/components";
import { Select } from "clearsense-ui/components/formik";
import {
  LeftMinimalArrow,
  RightMinimalArrow,
} from "components/Common/clearsense-ui/icons";
import { useMetaIQDispatch, useMetaIQState } from "contexts/metaIQ";

import {
  DATABASE_DETAILS_VALIDATION,
  DATABASE_TYPE,
  INITIAL_DATABASE_DETAILS,
} from "./constants";
import DatabaseDetails from "./DatabaseDetails";

const DatabaseType = ({ onClose, setStep }) => {
  const { categories, databaseDetails, existingOrNewDatabase } =
    useMetaIQState();
  const [details, setDetails] = useState(INITIAL_DATABASE_DETAILS);
  const metaIQDispatch = useMetaIQDispatch();
  const [existingOrNew, setExistingOrNew] = useState("new");

  const handleChangeType = type => {
    setExistingOrNew(type);
  };

  const handleChange = (name, value, setFieldValue) => {
    setFieldValue(name, value);
    setDetails({ ...details, [name]: value });
    metaIQDispatch({
      type: "SET_DATABASE_DETAILS",
      payload: { databaseDetails: { ...details, [name]: value } },
    });
  };

  const handleSubmit = () => {
    setStep("verify");
  };

  useEffect(() => {
    metaIQDispatch({
      type: "SET_EXISTING_OR_NEW_DATABASE",
      payload: { existingOrNewDatabase: existingOrNew },
    });
  }, [existingOrNew]);

  useEffect(() => {
    if (isEmpty(categories)) {
      setStep("categories");
    }

    if (!isEmpty(databaseDetails)) {
      setDetails(databaseDetails);
    }
    setExistingOrNew(existingOrNewDatabase);
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={details}
      validationSchema={DATABASE_DETAILS_VALIDATION}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, setFieldValue, isValid, values }) => (
        <Form>
          <Pane.Body className="py-2">
            <Steps
              className="mb-6"
              currentStep={2}
              steps={[
                <Typography
                  key="categories"
                  onClick={() => setStep("categories")}
                >
                  Categories
                </Typography>,
                <Typography key="database_details">
                  Database Details
                </Typography>,
                <Typography key="verify">Verify</Typography>,
              ]}
            />
            <div className="flex flex-col space-y-4 w-full">
              <Select
                label="Select Database Type"
                required
                name="db_type"
                placeholder="Select database type"
                options={DATABASE_TYPE}
                value={{ value: values.db_type, label: values.db_type }}
                onChange={value =>
                  handleChange("db_type", value.value, setFieldValue)
                }
              />
              {values.db_type && (
                <div className="flex flex-col space-y-4">
                  <Typography>Add Database Details</Typography>
                  <Radio stacked className="mt-8 space-y-4">
                    <Radio.Item
                      onChange={() => {
                        handleChangeType("existing");
                      }}
                      checked={existingOrNew === "existing"}
                      label="Choose an existing database"
                      value="existing"
                      name="application"
                      data-cy="add-existing-source-radio"
                    />
                    <Radio.Item
                      onChange={() => {
                        handleChangeType("new");
                      }}
                      checked={existingOrNew === "new"}
                      label="Connect to a new database"
                      value="new"
                      name="application"
                      data-cy="add-new-source-radio"
                    />
                  </Radio>
                  {existingOrNew === "new" && (
                    <DatabaseDetails
                      handleChange={handleChange}
                      databaseDetails={databaseDetails}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </div>
              )}
            </div>
          </Pane.Body>
          <Pane.Footer>
            <div className="flex justify-between w-full ">
              <Button label="Cancel" style="secondary" onClick={onClose} />
              <div className="flex space-x-2">
                <Button
                  label="Previous"
                  icon={LeftMinimalArrow}
                  style="secondary"
                  onClick={() => setStep("categories")}
                />
                <Button
                  label="Next"
                  type="submit"
                  icon={RightMinimalArrow}
                  iconPosition="right"
                  disabled={
                    isSubmitting || !isValid || isEmpty(databaseDetails)
                  }
                />
              </div>
            </div>
          </Pane.Footer>
        </Form>
      )}
    </Formik>
  );
};

export default DatabaseType;
